// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Initial true-up workflow page component.
 *
 * Provides a multi-step workflow for onboarding new organizations:
 * 1. Fill out the initial true-up form with GL balances
 * 2. Review the created close run with deltas and journal entry
 * 3. Confirm and lock the initial true-up
 *
 * If the organization has already completed initial true-up, displays a
 * success message indicating onboarding is complete.
 */
import { formatPersonName } from '@/account/utilities/format-person-name';
import { getInitialTrueUpCloseRun } from '@/accounting/api/get-initial-true-up-close-run';
import { InitialTrueUpCloseRunResponse } from '@/accounting/api/types';
import { CloseRunInfoBar } from '@/accounting/components/close-run-info-bar';
import { InitialTrueUpForm } from '@/accounting/components/initial-true-up-form';
import {
  BalanceComparisonTable,
  OnboardingJournalEntry,
  ReviewStep,
} from '@/accounting/components/initial-true-up-review-step';
import { useInitialTrueUps } from '@/accounting/hooks/use-initial-true-ups';
import { formatPeriod } from '@/accounting/utilities/generate-period-list';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Check,
  CheckCircleOutlineRounded,
  ChevronLeftRounded,
  DownloadRounded,
  InfoOutlined,
  LockOutlined,
  TuneOutlined,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { useDownloadFileByKey } from '@/hooks/use-download-file-by-key';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Stack,
  Step,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography,
  stepConnectorClasses,
  styled,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Workflow step type for tracking progress through the initial true-up flow.
 */
type WorkflowStep = 'form' | 'review' | 'locked';

/**
 * Step definitions for the initial true-up workflow stepper.
 */
const STEPS = [
  {
    id: 'form' as WorkflowStep,
    label: 'Enter GL balances',
    description: 'Provide your current general ledger balances',
  },
  {
    id: 'review' as WorkflowStep,
    label: 'Review & confirm',
    description: 'Review deltas and generated journal entry',
  },
  {
    id: 'locked' as WorkflowStep,
    label: 'Complete',
    description: 'Initial true-up is complete',
  },
];

/**
 * Maps workflow step to numeric index for the stepper.
 */
const STEP_INDEX: Record<WorkflowStep, number> = {
  form: 0,
  review: 1,
  locked: 2,
};

/**
 * Custom connector for the stepper, adjusted for larger step icons.
 */
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15, // Center with 30px icons
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
  },
}));

/**
 * Custom step icon with larger circles and bolder numbers.
 */
function CustomStepIcon(props: StepIconProps) {
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 700,
        fontSize: '18px',
        backgroundColor: props.completed
          ? 'success.main'
          : props.active
            ? 'primary.main'
            : 'background.paper',
        border: (theme) =>
          `2px solid ${
            props.completed
              ? theme.palette.success.main
              : props.active
                ? theme.palette.primary.main
                : theme.palette.divider
          }`,
        color: props.completed || props.active ? 'white' : 'text.secondary',
      }}
    >
      {props.completed ? <Check /> : props.icon}
    </Box>
  );
}

/**
 * Joins ISO codes for prose: "EUR", "EUR and GBP", "EUR, GBP and JPY".
 */
function formatCurrencyList(currencyCodes: string[]): string {
  if (currencyCodes.length <= 1) {
    return currencyCodes[0] ?? '';
  }
  return `${currencyCodes.slice(0, -1).join(', ')} and ${currencyCodes[currencyCodes.length - 1]}`;
}

/**
 * Per-currency initial true-up status board with an initial true-up action for each missing currency.
 */
function OnboardingCompleteView() {
  const { initialTrueUps, isLoading: isLoadingInitialTrueUps } =
    useInitialTrueUps();
  const { user } = useCurrentUser();
  const organizationName = user.organization.name;

  const lockedInitialTrueUps = useMemo(
    () => initialTrueUps?.initialTrueUps ?? [],
    [initialTrueUps]
  );
  const missingCurrencies = useMemo(
    () => initialTrueUps?.missingCurrencies ?? [],
    [initialTrueUps]
  );
  const lockedCurrencyCodes = useMemo(
    () => new Set(lockedInitialTrueUps.map((lock) => lock.currency)),
    [lockedInitialTrueUps]
  );
  const assetCurrencyCount =
    lockedInitialTrueUps.length + missingCurrencies.length;
  const allTruedUp = assetCurrencyCount > 0 && missingCurrencies.length === 0;

  // Each locked currency may live in a different run/period, so fetch every
  // distinct run to render all of their balances and journal entries.
  const runIds = [
    ...new Set(lockedInitialTrueUps.map((lock) => lock.closeRunId)),
  ];
  const runResults = useQueries({
    queries: runIds.map((id) => ({
      queryKey: ['accounting', 'initial-true-up', 'close-run', id],
      queryFn: () => getInitialTrueUpCloseRun(id).then((run) => run.closeRun),
    })),
  });
  const runsById = new Map<string, InitialTrueUpCloseRunResponse>();
  runResults.forEach((result, index) => {
    if (result.data) {
      runsById.set(runIds[index], result.data);
    }
  });
  const isLoadingRuns = runResults.some((result) => result.isLoading);
  const isRunLoadError = runResults.some((result) => result.isError);
  const [isRunLoadErrorDismissed, setIsRunLoadErrorDismissed] = useState(false);

  const navigate = useNavigate();
  const downloadPeriodSummary = useDownloadFileByKey();

  // Not-yet-trued currencies start expanded (to surface their CTA); trued-up ones
  // start collapsed, including one just completed.
  const [expandedCurrencyCodes, setExpandedCurrencyCodes] = useState<
    Set<string>
  >(new Set());
  const [didInitExpansion, setDidInitExpansion] = useState(false);
  useEffect(() => {
    if (didInitExpansion || assetCurrencyCount === 0) {
      return;
    }

    setExpandedCurrencyCodes(new Set<string>(missingCurrencies));
    setDidInitExpansion(true);
  }, [assetCurrencyCount, missingCurrencies, didInitExpansion]);

  const handleToggleSection = (code: string) => {
    setExpandedCurrencyCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  // Currencies still needing an initial true-up sort to the top (they carry the CTA); the server
  // sorts both lists, so each group is alphabetical.
  const truedUpCurrencies = lockedInitialTrueUps.map((lock) => lock.currency);
  const currencyBuckets = [...missingCurrencies, ...truedUpCurrencies].map(
    (code) => ({ isoCode: code })
  );

  // Otherwise the chip reads "0 of 0" before currencies load.
  if (isLoadingInitialTrueUps) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight={200}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading asset currencies...
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <ReportBreadcrumbs title="Initial true-up" parent="accounting" />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5">Initial subledger true-up</Typography>
          <Chip
            label={`${lockedCurrencyCodes.size} of ${assetCurrencyCount} asset currencies trued up`}
            color={allTruedUp ? 'success' : 'default'}
            variant={allTruedUp ? 'filled' : 'outlined'}
          />
        </Stack>
      </Stack>

      {allTruedUp ? (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineRounded />}
          sx={{ p: 2 }}
        >
          <AlertTitle paragraph>Onboarding complete</AlertTitle>
          <Typography variant="body2">
            Every asset currency has been trued up. Hardfin is the authoritative
            subledger for your asset accounting.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info" icon={<InfoOutlined />}>
          <AlertTitle>Onboarding in progress</AlertTitle>
          <Typography variant="body2">
            {lockedCurrencyCodes.size} of {assetCurrencyCount} asset currencies
            are trued up. Set up {formatCurrencyList(missingCurrencies)} to
            finish onboarding. Each currency must be trued up individually to
            match the latest locked period for the organization.
          </Typography>
        </Alert>
      )}

      <PerCurrencySections
        buckets={currencyBuckets}
        prominentHeader
        alwaysAccordion
        sectionIdPrefix="onboarding-currency"
        expandedCurrencyCodes={expandedCurrencyCodes}
        onToggleSection={handleToggleSection}
        renderHeaderChip={(bucket) =>
          lockedCurrencyCodes.has(bucket.isoCode) ? (
            <Chip size="small" color="success" label="Trued up" />
          ) : (
            <Chip
              size="small"
              variant="outlined"
              label="Needs initial true-up"
            />
          )
        }
        renderBucket={(bucket) => {
          const code = bucket.isoCode;
          const lock = lockedInitialTrueUps.find(
            (item) => item.currency === code
          );
          if (!lock) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  pl: 2,
                }}
              >
                <Alert severity="warning">
                  <Typography variant="body2">
                    {organizationName}{' '}
                    <Link
                      to="/assets"
                      search={{ currency: code }}
                      style={{ color: 'inherit' }}
                    >
                      has {code} assets
                    </Link>{' '}
                    but the initial true-up is still pending for {code}
                  </Typography>
                </Alert>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<TuneOutlined fontSize="small" />}
                  // Sits beside an alert whose copy grows with the organization name, so
                  // it holds its width rather than wrapping the label.
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  onClick={() =>
                    navigate({
                      to: '/accounting/initial-true-up/$iso',
                      params: { iso: code },
                    })
                  }
                >
                  {`Initial true-up ${code}`}
                </Button>
              </Box>
            );
          }

          const run = runsById.get(lock.closeRunId);
          const entry = run?.currency === code ? run : undefined;
          if (!entry) {
            return isLoadingRuns ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={24} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Loading initial true-up details...
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Balance detail unavailable for {code}.
              </Typography>
            );
          }
          const periodEndDate = DateTime.fromObject({
            year: lock.accountingPeriod.year,
            month: lock.accountingPeriod.month,
          }).endOf('month');
          return (
            <Stack spacing={2}>
              <Alert
                severity="success"
                icon={<CheckCircleOutlineRounded />}
                sx={{ p: 2 }}
              >
                <AlertTitle paragraph>
                  Initial true-up complete for {code}
                </AlertTitle>
                <Typography variant="body1" paragraph>
                  Your organization has completed the initial true-up for {code}
                  .
                </Typography>
                <Stack direction="row" spacing={5}>
                  <Stack spacing={1}>
                    <Typography variant="body1" color="text.secondary">
                      Locked on{' '}
                      <strong>
                        {lock.lockedAt.toLocaleString(DateTime.DATE_FULL)}
                      </strong>{' '}
                      by <strong>{formatPersonName(lock.lockedBy)}</strong>
                    </Typography>
                    {run?.lockedSignature && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-start"
                        alignItems="center"
                      >
                        <Typography variant="body1" color="text.secondary">
                          Signature:
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          fontFamily="cursive"
                        >
                          {run.lockedSignature}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body1" color="text.secondary">
                      Initial true-up period:{' '}
                      <strong>{formatPeriod(lock.accountingPeriod)}</strong>
                    </Typography>
                    {run?.lockedNotes && (
                      <Typography variant="body1" color="text.secondary">
                        Notes: <strong>{run.lockedNotes}</strong>
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Alert>
              {/* Balance comparison paper, with the period summary download inside */}
              <Card variant="outlined">
                <CardHeader
                  avatar={<LockOutlined fontSize="small" color="disabled" />}
                  title="Balance comparison"
                  subheader={`As of ${periodEndDate.toLocaleString(DateTime.DATE_FULL)}`}
                  action={
                    run && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={
                          downloadPeriodSummary.isPending &&
                          downloadPeriodSummary.variables ===
                            run.excelFileKey ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DownloadRounded fontSize="small" />
                          )
                        }
                        disabled={
                          !run.excelFileKey || downloadPeriodSummary.isPending
                        }
                        onClick={() => {
                          if (run.excelFileKey) {
                            downloadPeriodSummary.mutate(run.excelFileKey);
                          }
                        }}
                      >
                        Download period summary
                      </Button>
                    )
                  }
                  sx={{ paddingTop: 3, paddingBottom: 1 }}
                />
                <CardContent>
                  <BalanceComparisonTable
                    currencyCode={code}
                    glBalances={entry.glBalances}
                    hardfinBalances={entry.hardfinBalances}
                    deltas={entry.deltas}
                  />
                </CardContent>
              </Card>
              <OnboardingJournalEntry
                currencyCode={code}
                journalEntry={entry.journalEntry}
                isLocked
              />
              <CloseRunInfoBar
                closeRunKey={entry.key}
                createdAt={entry.createdAt}
                period={entry.period}
                createdBy={entry.createdBy}
              />
            </Stack>
          );
        }}
      />
      <SupportSnackbar
        isOpen={isRunLoadError && !isRunLoadErrorDismissed}
        onClose={() => setIsRunLoadErrorDismissed(true)}
        message="An error occurred while loading the initial true-up balances."
      />
    </Stack>
  );
}

/**
 * Main initial true-up workflow page component.
 * Manages the multi-step workflow state and renders the appropriate view
 * based on the current step and organization's initial true-up status.
 */
export function InitialTrueUpWorkflowPage(props: {
  currencyCode?: IsoCurrencyCode;
}) {
  const { isLoading, isError } = useInitialTrueUps();
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('form');
  const [closeRun, setCloseRun] =
    useState<InitialTrueUpCloseRunResponse | null>(null);
  // Ref for scrolling to top when changing steps
  const topAnchorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Show loading state while checking initial true-up status
  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" minHeight={200}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Checking initial true-up status...
        </Typography>
      </Stack>
    );
  }

  // Show error state if status check failed
  if (isError) {
    return (
      <Alert severity="error">
        Failed to check initial true-up status. Please try refreshing the page.
      </Alert>
    );
  }

  // Without a currency in the path, show the status board.
  if (props.currencyCode === undefined) {
    return <OnboardingCompleteView />;
  }
  const selectedCurrencyCode = props.currencyCode;

  // Handle successful form submission
  const handleFormSuccess = (
    createdCloseRun: InitialTrueUpCloseRunResponse
  ) => {
    setCloseRun(createdCloseRun);
    setCurrentStep('review');
    // Scroll to top after React re-renders with the new step
    topAnchorRef.current?.scrollIntoView({ behavior: 'instant' });
  };

  // Handle going back to form
  const handleBackToForm = () => {
    setCurrentStep('form');
    setCloseRun(null);
  };

  const handleBackToSummary = () => {
    navigate({ to: '/accounting/initial-true-up' });
  };

  // Handle successful lock
  const handleLockSuccess = () => {
    navigate({ to: '/accounting/initial-true-up' });
  };

  const showBackToSummary = currentStep === 'form';

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 'form':
        return (
          <Stack spacing={3}>
            <Alert severity="success">
              {`The initial true-up establishes Hardfin as an authoritative subledger for ${selectedCurrencyCode} by creating a one-time true-up journal entry to align your ${selectedCurrencyCode} general ledger with Hardfin's computed balances.`}
            </Alert>
            <InitialTrueUpForm
              key={selectedCurrencyCode}
              currencyCode={selectedCurrencyCode}
              onSuccess={handleFormSuccess}
            />
          </Stack>
        );

      case 'review':
        if (!closeRun) {
          // This shouldn't happen, but handle gracefully
          setCurrentStep('form');
          return null;
        }
        return (
          <ReviewStep
            closeRun={closeRun}
            onBack={handleBackToForm}
            onLockSuccess={handleLockSuccess}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Stack spacing={3}>
      {/* Anchor for scrolling to top when changing steps */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        ref={topAnchorRef}
      >
        <Stack spacing={1}>
          <ReportBreadcrumbs title="Initial true-up" parent="accounting" />
          <Typography variant="h5">
            {`Run initial true-up for ${selectedCurrencyCode}`}
          </Typography>
        </Stack>
        {showBackToSummary && (
          <Button
            variant="text"
            color="primary"
            size="small"
            startIcon={<ChevronLeftRounded fontSize="small" />}
            onClick={handleBackToSummary}
          >
            Back to summary
          </Button>
        )}
      </Stack>

      <Card variant="outlined">
        <CardContent sx={{ paddingTop: 3 }}>
          <Stepper
            activeStep={STEP_INDEX[currentStep]}
            alternativeLabel
            connector={<CustomConnector />}
          >
            {STEPS.map((step) => {
              const stepIndex = STEP_INDEX[step.id];
              const currentIndex = STEP_INDEX[currentStep];
              const isCompleted = stepIndex < currentIndex;

              return (
                <Step key={step.id} completed={isCompleted}>
                  <StepLabel
                    StepIconComponent={CustomStepIcon}
                    optional={
                      <Typography variant="caption">
                        {step.description}
                      </Typography>
                    }
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </CardContent>
      </Card>

      <Box>{renderStepContent()}</Box>
    </Stack>
  );
}
